import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * The assistant's answer, as markdown in the chat's own type scale (the AI message sets text-meta).
 * Raw HTML is never rendered (react-markdown escapes it). Images are dropped, so an answer the model
 * was steered into cannot make the browser fetch a URL on its own, and links are shown as their
 * text, never as links: the assistant has nothing to link to, and a link inside a "Pertento"
 * message is exactly what an instruction planted in the shared thread would ask for (a phishing
 * page dressed as the product).
 */
const COMPONENTS: Components = {
  p: ({ children }) => <p>{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-4">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-4">{children}</ol>,
  // The prompt asks for no headings; any that come read as a bold line, never a page title.
  h1: ({ children }) => <p className="font-semibold">{children}</p>,
  h2: ({ children }) => <p className="font-semibold">{children}</p>,
  h3: ({ children }) => <p className="font-semibold">{children}</p>,
  h4: ({ children }) => <p className="font-semibold">{children}</p>,
  code: ({ children }) => <code className="rounded-sm bg-field px-1 font-mono text-micro">{children}</code>,
  a: ({ children }) => <span data-results-ai-link="">{children}</span>,
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-micro">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b border-line px-2 py-1 text-left font-medium text-dim">{children}</th>,
  td: ({ children }) => <td className="border-b border-line px-2 py-1 font-mono">{children}</td>,
};

type TestLinks = {
  /** The tests the server checked the answer may name: only these become links. */
  ids: ReadonlySet<number>;
  onOpen: (experimentId: number) => void;
};

type AnswerMarkdownProps = {
  text: string;
  /**
   * The learnings Ask's answers name tests as #1234, and a checked one opens its debrief in the
   * page. Every other link stays plain text, as above: only an in-app button for an id the server
   * verified, never a URL.
   */
  testLinks?: TestLinks;
};

const TEST_HREF = /^#test-(\d+)$/;

/** Each checked #1234 as a markdown link to #test-1234, which the `a` below turns into a button. */
const linkTests = (text: string, ids: ReadonlySet<number>): string =>
  text.replace(/(^|[^[\w])#(\d+)\b/g, (match, before: string, id: string) =>
    ids.has(Number(id)) ? `${before}[#${id}](#test-${id})` : match,
  );

const componentsFor = (testLinks: TestLinks | undefined): Components =>
  testLinks
    ? {
        ...COMPONENTS,
        a: ({ href, children }) => {
          const id = Number(TEST_HREF.exec(href ?? '')?.[1]);
          // Labelled from the id it opens, never from the link's text, so it cannot say one test
          // and open another.
          return testLinks.ids.has(id) ? (
            <button
              type="button"
              onClick={() => testLinks.onOpen(id)}
              className="font-medium text-accent hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              #{id}
            </button>
          ) : (
            <span data-results-ai-link="">{children}</span>
          );
        },
      }
    : COMPONENTS;

export const AnswerMarkdown = ({ text, testLinks }: AnswerMarkdownProps) => (
  <div data-results-ai-answer="" className="space-y-2 break-words">
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={componentsFor(testLinks)}
      disallowedElements={['img']}
      unwrapDisallowed={false}
    >
      {testLinks ? linkTests(text, testLinks.ids) : text}
    </Markdown>
  </div>
);
